import React, { DetailedHTMLProps, SelectHTMLAttributes } from 'react'
import { useFormContext } from 'react-hook-form'
import clsx from 'clsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleExclamation } from '@fortawesome/pro-regular-svg-icons'

type QuizDropdownInputProps = {
  name: string
  label: string
  options: Array<{ id: string; label: string }>
  hasError?: boolean
} & DetailedHTMLProps<
  SelectHTMLAttributes<HTMLSelectElement>,
  HTMLSelectElement
>

export default function QuizDropdownInput({
  name,
  label,
  options,
  hasError,

  ...props
}: QuizDropdownInputProps) {
  const { register, watch, formState } = useFormContext()
  const value = watch(name)
  const field = register(name)
  const { isSubmitting, isSubmitted, errors, touchedFields } = formState
  const touched = touchedFields?.[name] || isSubmitted
  const error = errors[name]
  const isDisabled = isSubmitting || props.disabled

  hasError = (touched && error) || hasError

  return (
    <div>
      <div className="relative flex items-center">
        <select
          id={name}
          {...props}
          {...field}
          disabled={isDisabled}
          aria-invalid={Boolean(hasError)}
          aria-describedby={touched && error ? `${name}-error` : undefined}
          className={clsx(
            'peer block h-[4.5rem] w-full rounded-2xl border-0 px-4 pb-0 text-base ring-2 ring-inset focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:leading-6',
            hasError ? 'ring-red-600' : 'ring-gray-300',
            isDisabled ? 'bg-gray-300' : '',

            // If no value is selected, we want to show the empty label in placeholder color
            value ? 'text-gray-900' : '',
          )}
        >
          {options.map(({ id, label }) => (
            <option key={`${name}-${id}`} value={id}>
              {label}
            </option>
          ))}
        </select>
        <label
          htmlFor={name}
          className={clsx(
            'absolute origin-top-left pl-4 text-base text-gray-400 transition-all duration-300',

            // If the field is not empty, leave the label up top
            value ? '-translate-y-3 translate-x-1 scale-75' : '',
          )}
        >
          {label}
        </label>
      </div>

      {touched && error && (
        <div
          id={`${name}-error`}
          className="relative mt-1 block pl-4 text-sm text-red-600"
        >
          <FontAwesomeIcon icon={faCircleExclamation} className="mr-1.5" />
          {`${error.message}`}
        </div>
      )}
    </div>
  )
}
